import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  Settings,
  Search,
  FolderPlus,
  X,
} from 'lucide-react';
import { Button, Card, Badge, Modal, Spinner, useToast } from '@centraoneplus/ui';
import type { FieldDefinition, FieldType, TicketType } from '@centraoneplus/shared-types';
import { fieldDefinitionsService } from '@services/field-definitions.service';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Types for field group configuration
export interface FieldGroupConfig {
  id: string;
  key: string;
  name: string;
  description?: string;
  icon?: string;
  displayLocation: 'main' | 'sidebar' | 'modal';
  isCollapsible: boolean;
  isCollapsedByDefault: boolean;
  sortOrder: number;
  fields: FieldConfig[];
}

export interface FieldConfig {
  fieldDefinitionId: string;
  fieldDefinition?: FieldDefinition;
  isRequired: boolean;
  isVisible: boolean;
  isVisibleInList: boolean;
  sortOrder: number;
  stageVisibility?: Record<string, boolean>;
}

interface FieldConfigurationPanelProps {
  ticketType?: TicketType;
  fieldGroups: FieldGroupConfig[];
  onChange: (fieldGroups: FieldGroupConfig[]) => void;
  isLoading?: boolean;
  stages?: { id: string; name: string }[];
}

// Field type labels for display
const fieldTypeLabels: Record<FieldType, string> = {
  text: 'Text',
  textarea: 'Long Text',
  number: 'Number',
  email: 'Email',
  phone: 'Phone',
  url: 'URL',
  date: 'Date',
  datetime: 'Date & Time',
  select: 'Dropdown',
  multiselect: 'Multi-Select',
  checkbox: 'Checkbox',
  radio: 'Radio Buttons',
};

// Display location options
const displayLocations = [
  { value: 'main', label: 'Main Panel' },
  { value: 'sidebar', label: 'Sidebar' },
  { value: 'modal', label: 'Modal/Dialog' },
];

type DisplayLocation = 'main' | 'sidebar' | 'modal';

interface NewGroupFormData {
  name: string;
  description: string;
  displayLocation: DisplayLocation;
  isCollapsible: boolean;
  isCollapsedByDefault: boolean;
}

const defaultNewGroupData: NewGroupFormData = {
  name: '',
  description: '',
  displayLocation: 'main',
  isCollapsible: true,
  isCollapsedByDefault: false,
};

export function FieldConfigurationPanel({
  fieldGroups,
  onChange,
  isLoading,
  stages = [],
}: FieldConfigurationPanelProps) {
  const toast = useToast();
  // Use ref for toast to prevent loadFields from changing when toast reference changes
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [availableFields, setAvailableFields] = useState<FieldDefinition[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(true);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [editingField, setEditingField] = useState<{ groupIndex: number; fieldIndex: number } | null>(null);

  // New group form state
  const [newGroupData, setNewGroupData] = useState<NewGroupFormData>(defaultNewGroupData);

  // Load available field definitions
  const loadFields = useCallback(async () => {
    try {
      setIsLoadingFields(true);
      const fields = await fieldDefinitionsService.getFieldDefinitions({ isActive: true });
      setAvailableFields(fields);
    } catch (error) {
      console.error('Failed to load field definitions:', error);
      toastRef.current.error('Error', 'Failed to load available fields');
    } finally {
      setIsLoadingFields(false);
    }
  }, []); // Empty dependency array - loadFields is now stable

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  // Get fields that are already assigned to any group
  const assignedFieldIds = new Set(
    fieldGroups.flatMap((group) => group.fields.map((f) => f.fieldDefinitionId))
  );

  // Filter available fields based on search and already assigned
  const filteredAvailableFields = availableFields.filter(
    (field) =>
      !assignedFieldIds.has(field.id) &&
      (field.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
        field.fieldType.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Toggle group expansion
  const toggleGroupExpand = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  // Add a new field group
  const handleAddGroup = () => {
    console.log('[FieldConfigurationPanel] handleAddGroup called');
    console.log('[FieldConfigurationPanel] newGroupData:', newGroupData);

    if (!newGroupData.name.trim()) {
      console.log('[FieldConfigurationPanel] Validation failed - name is empty');
      toast.error('Error', 'Group name is required');
      return;
    }

    const key = newGroupData.name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_');

    const newGroup: FieldGroupConfig = {
      id: `group_${Date.now()}`,
      key,
      name: newGroupData.name.trim(),
      description: newGroupData.description,
      displayLocation: newGroupData.displayLocation,
      isCollapsible: newGroupData.isCollapsible,
      isCollapsedByDefault: newGroupData.isCollapsedByDefault,
      sortOrder: fieldGroups.length,
      fields: [],
    };

    console.log('[FieldConfigurationPanel] Created newGroup:', newGroup);
    console.log('[FieldConfigurationPanel] Current fieldGroups count:', fieldGroups.length);

    onChange([...fieldGroups, newGroup]);
    console.log('[FieldConfigurationPanel] Called onChange with new fieldGroups');

    setExpandedGroups((prev) => new Set([...prev, newGroup.id]));
    setShowAddGroupModal(false);
    setNewGroupData(defaultNewGroupData);
    toast.success('Success', 'Field group added');
    console.log('[FieldConfigurationPanel] handleAddGroup completed');
  };

  // Remove a field group
  const handleRemoveGroup = (groupIndex: number) => {
    const updatedGroups = fieldGroups.filter((_, idx) => idx !== groupIndex);
    // Update sort orders
    updatedGroups.forEach((group, idx) => {
      group.sortOrder = idx;
    });
    onChange(updatedGroups);
    toast.success('Success', 'Field group removed');
  };

  // Move group up/down
  const handleMoveGroup = (groupIndex: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && groupIndex === 0) ||
      (direction === 'down' && groupIndex === fieldGroups.length - 1)
    ) {
      return;
    }

    const updatedGroups = [...fieldGroups];
    const targetIndex = direction === 'up' ? groupIndex - 1 : groupIndex + 1;
    [updatedGroups[groupIndex], updatedGroups[targetIndex]] = [
      updatedGroups[targetIndex],
      updatedGroups[groupIndex],
    ];

    // Update sort orders
    updatedGroups.forEach((group, idx) => {
      group.sortOrder = idx;
    });

    onChange(updatedGroups);
  };

  // Add field to a group
  const handleAddField = (field: FieldDefinition) => {
    if (selectedGroupIndex === null) return;

    const updatedGroups = [...fieldGroups];
    const group = updatedGroups[selectedGroupIndex];

    const newFieldConfig: FieldConfig = {
      fieldDefinitionId: field.id,
      fieldDefinition: field,
      isRequired: false,
      isVisible: true,
      isVisibleInList: false,
      sortOrder: group.fields.length,
    };

    group.fields.push(newFieldConfig);
    onChange(updatedGroups);
    setShowAddFieldModal(false);
    toast.success('Success', `Field "${field.name}" added to "${group.name}"`);
  };

  // Remove field from a group
  const handleRemoveField = (groupIndex: number, fieldIndex: number) => {
    const updatedGroups = [...fieldGroups];
    updatedGroups[groupIndex].fields.splice(fieldIndex, 1);

    // Update sort orders
    updatedGroups[groupIndex].fields.forEach((field, idx) => {
      field.sortOrder = idx;
    });

    onChange(updatedGroups);
    toast.success('Success', 'Field removed from group');
  };

  // Move field within a group
  const handleMoveField = (groupIndex: number, fieldIndex: number, direction: 'up' | 'down') => {
    const group = fieldGroups[groupIndex];
    if (
      (direction === 'up' && fieldIndex === 0) ||
      (direction === 'down' && fieldIndex === group.fields.length - 1)
    ) {
      return;
    }

    const updatedGroups = [...fieldGroups];
    const fields = updatedGroups[groupIndex].fields;
    const targetIndex = direction === 'up' ? fieldIndex - 1 : fieldIndex + 1;
    [fields[fieldIndex], fields[targetIndex]] = [fields[targetIndex], fields[fieldIndex]];

    // Update sort orders
    fields.forEach((field, idx) => {
      field.sortOrder = idx;
    });

    onChange(updatedGroups);
  };

  // Toggle field visibility
  const handleToggleFieldVisibility = (groupIndex: number, fieldIndex: number) => {
    const updatedGroups = [...fieldGroups];
    const field = updatedGroups[groupIndex].fields[fieldIndex];
    field.isVisible = !field.isVisible;
    onChange(updatedGroups);
  };

  // Toggle field required
  const handleToggleFieldRequired = (groupIndex: number, fieldIndex: number) => {
    const updatedGroups = [...fieldGroups];
    const field = updatedGroups[groupIndex].fields[fieldIndex];
    field.isRequired = !field.isRequired;
    onChange(updatedGroups);
  };

  // Update group settings
  const handleUpdateGroup = (groupIndex: number, updates: Partial<FieldGroupConfig>) => {
    const updatedGroups = [...fieldGroups];
    updatedGroups[groupIndex] = { ...updatedGroups[groupIndex], ...updates };
    onChange(updatedGroups);
  };

  if (isLoading || isLoadingFields) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Field Configuration</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">Configure which fields appear on this ticket type</p>
        </div>
        <Button onClick={() => {
          console.log('[FieldConfigurationPanel] Add Field Group button clicked');
          console.log('[FieldConfigurationPanel] showAddGroupModal before:', showAddGroupModal);
          setShowAddGroupModal(true);
        }}>
          <FolderPlus className="h-4 w-4 mr-2" />
          Add Field Group
        </Button>
      </div>

      {/* Field Groups */}
      {fieldGroups.length === 0 ? (
        <Card className="p-8 text-center">
          <FolderPlus className="h-12 w-12 mx-auto text-gray-400 mb-4 dark:text-gray-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No Field Groups
          </h3>
          <p className="text-gray-500 mb-4 dark:text-gray-400">
            Add field groups to organize custom fields on this ticket type.
          </p>
          <Button onClick={() => setShowAddGroupModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Group
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {fieldGroups.map((group, groupIndex) => {
            const isExpanded = expandedGroups.has(group.id);

            return (
              <Card key={group.id} className="overflow-hidden">
                {/* Group Header */}
                <div
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 cursor-pointer"
                  onClick={() => toggleGroupExpand(group.id)}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">
                          {group.name}
                        </h4>
                        <Badge variant="secondary" size="sm">
                          {group.fields.length} fields
                        </Badge>
                        <Badge variant="outline" size="sm">
                          {displayLocations.find((l) => l.value === group.displayLocation)?.label}
                        </Badge>
                      </div>
                      {group.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{group.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveGroup(groupIndex, 'up');
                      }}
                      disabled={groupIndex === 0}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveGroup(groupIndex, 'down');
                      }}
                      disabled={groupIndex === fieldGroups.length - 1}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveGroup(groupIndex);
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Group Content */}
                {isExpanded && (
                  <div className="p-4 space-y-4">
                    {/* Group Settings */}
                    <div className="grid grid-cols-3 gap-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Display Location
                        </label>
                        <ThemedSelect
                          value={group.displayLocation}
                          onChange={(e) =>
                            handleUpdateGroup(groupIndex, {
                              displayLocation: e.target.value as 'main' | 'sidebar' | 'modal',
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          {displayLocations.map((loc) => (
                            <option key={loc.value} value={loc.value}>
                              {loc.label}
                            </option>
                          ))}
                        </ThemedSelect>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={group.isCollapsible}
                            onChange={(e) =>
                              handleUpdateGroup(groupIndex, { isCollapsible: e.target.checked })
                            }
                            className="rounded"
                          />
                          <span className="text-gray-700 dark:text-gray-300">Collapsible</span>
                        </label>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={group.isCollapsedByDefault}
                            disabled={!group.isCollapsible}
                            onChange={(e) =>
                              handleUpdateGroup(groupIndex, { isCollapsedByDefault: e.target.checked })
                            }
                            className="rounded"
                          />
                          <span className="text-gray-700 dark:text-gray-300">Collapsed by default</span>
                        </label>
                      </div>
                    </div>

                    {/* Fields List */}
                    {group.fields.length === 0 ? (
                      <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                        <p className="mb-2">No fields in this group</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedGroupIndex(groupIndex);
                            setShowAddFieldModal(true);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Field
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {group.fields.map((fieldConfig, fieldIndex) => {
                          const field = fieldConfig.fieldDefinition ||
                            availableFields.find((f) => f.id === fieldConfig.fieldDefinitionId);

                          return (
                            <div
                              key={fieldConfig.fieldDefinitionId}
                              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                            >
                              <GripVertical className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900 dark:text-gray-100">
                                    {field?.name || 'Unknown Field'}
                                  </span>
                                  <Badge variant="secondary" size="sm">
                                    {field?.fieldType ? fieldTypeLabels[field.fieldType] : 'Unknown'}
                                  </Badge>
                                  {fieldConfig.isRequired && (
                                    <Badge variant="warning" size="sm">Required</Badge>
                                  )}
                                  {!fieldConfig.isVisible && (
                                    <Badge variant="default" size="sm">Hidden</Badge>
                                  )}
                                </div>
                                {field?.description && (
                                  <p className="text-xs text-gray-500 truncate dark:text-gray-400">{field.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleFieldVisibility(groupIndex, fieldIndex)}
                                  title={fieldConfig.isVisible ? 'Hide field' : 'Show field'}
                                >
                                  {fieldConfig.isVisible ? (
                                    <Eye className="h-4 w-4" />
                                  ) : (
                                    <EyeOff className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleFieldRequired(groupIndex, fieldIndex)}
                                  title={fieldConfig.isRequired ? 'Make optional' : 'Make required'}
                                  className={fieldConfig.isRequired ? 'text-amber-500' : ''}
                                >
                                  <span className="text-xs font-bold">*</span>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveField(groupIndex, fieldIndex, 'up')}
                                  disabled={fieldIndex === 0}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleMoveField(groupIndex, fieldIndex, 'down')}
                                  disabled={fieldIndex === group.fields.length - 1}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveField(groupIndex, fieldIndex)}
                                >
                                  <X className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Add Field Button */}
                    {group.fields.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedGroupIndex(groupIndex);
                          setShowAddFieldModal(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Field
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Field Modal */}
      <Modal
        isOpen={showAddFieldModal}
        onClose={() => {
          setShowAddFieldModal(false);
          setSearchQuery('');
        }}
        title="Add Field to Group"
        size="md"
      >
        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search fields..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
          </div>

          {/* Available Fields */}
          <div className="max-h-96 overflow-y-auto">
            {filteredAvailableFields.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No matching fields found' : 'All fields are already assigned'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredAvailableFields.map((field) => (
                  <div
                    key={field.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    onClick={() => handleAddField(field)}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {field.name}
                        </span>
                        <Badge variant="secondary" size="sm">
                          {fieldTypeLabels[field.fieldType]}
                        </Badge>
                        {field.isSystem && (
                          <Badge variant="info" size="sm">System</Badge>
                        )}
                      </div>
                      {field.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{field.description}</p>
                      )}
                    </div>
                    <Plus className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* Add Group Modal */}
      <Modal
        isOpen={showAddGroupModal}
        onClose={() => {
          setShowAddGroupModal(false);
          setNewGroupData(defaultNewGroupData);
        }}
        title="Add Field Group"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Group Name *
            </label>
            <input
              type="text"
              value={newGroupData.name}
              onChange={(e) => setNewGroupData({ ...newGroupData, name: e.target.value })}
              placeholder="e.g., Additional Details"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={newGroupData.description}
              onChange={(e) => setNewGroupData({ ...newGroupData, description: e.target.value })}
              placeholder="Optional description for this group"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Display Location
            </label>
            <ThemedSelect
              value={newGroupData.displayLocation}
              onChange={(e) =>
                setNewGroupData({
                  ...newGroupData,
                  displayLocation: e.target.value as DisplayLocation,
                })
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {displayLocations.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </ThemedSelect>
          </div>

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newGroupData.isCollapsible}
                onChange={(e) =>
                  setNewGroupData({ ...newGroupData, isCollapsible: e.target.checked })
                }
                className="rounded"
              />
              <span className="text-gray-700 dark:text-gray-300">Collapsible</span>
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newGroupData.isCollapsedByDefault}
                disabled={!newGroupData.isCollapsible}
                onChange={(e) =>
                  setNewGroupData({ ...newGroupData, isCollapsedByDefault: e.target.checked })
                }
                className="rounded"
              />
              <span className="text-gray-700 dark:text-gray-300">Collapsed by default</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddGroupModal(false);
                setNewGroupData(defaultNewGroupData);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAddGroup}>Add Group</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default FieldConfigurationPanel;
